import React, { PropsWithChildren, useEffect, useRef, useState } from 'react';
import { PanResponder, Platform, StyleSheet, View } from 'react-native';
import constants from '@/config/constants';
import { localLogoutUser } from '../api/services/user';
import { getCurrentRoute } from '../navigations/RootNavigation';
import {
  clearLocalData,
  getAccessToken,
  serverLogoutUser,
} from '../stores/auth/services';
import store from '../stores/store';
import utils from '../utils/utils';
import { ScreenshotPrevention } from '@/components/ScreenshotPrevention';
import { SafeAreaProvider, SafeAreaView } from 'react-native-safe-area-context';

// Set max Idle time after that time tracking stops 1 min
const maxIdleTimeToEndSession = 60 * 1000; // 1 minute
const maxIdleTimeToEndLogoutUser =
  Platform.OS === 'web' ? 20 * 60 * 1000 : 30 * 60 * 1000; // 20 minutes browser and 30 minutes tablets/mobiles

const styles = StyleSheet.create({
  layoutChildren: { flex: 1 },
});

export function Layout({ children }: PropsWithChildren) {
  // eslint-disable-next-line
  const timerForLogoutUserId = useRef<NodeJS.Timeout | undefined>();
  const timerForEndUserSessionId = useRef<NodeJS.Timeout | undefined>();
  const [timeForInactivityToLogoutUserInSecond] = useState(
    maxIdleTimeToEndLogoutUser,
  );
  const [timeForInactivityToEndSessionInSecond] = useState(
    maxIdleTimeToEndSession,
  );
  // watcher for when user logs in or out
  useEffect(() => {
    if (!store.getState().stayLoggedIn && store.getState().isAuthenticated) {
      resetLogoutInactivityTimeout();
    }

    if (!store.getState().isAuthenticated) {
      clearTimeout(timerForLogoutUserId.current);
      clearTimeout(timerForEndUserSessionId.current);
    }
  }, [store.getState().isAuthenticated]);

  const panResponder = React.useRef(
    PanResponder.create({
      onStartShouldSetPanResponderCapture: () => {
        // start new session when user is back from idle
        if (
          getCurrentRoute()?.name === 'ExerciseProcess' &&
          !store.getState().resultModalIsShowing
        ) {
          utils.startPracticeSessionTime();
        }

        // Track users who signed in without checkbox to stay logged in to log them out
        if (
          !store.getState().stayLoggedIn &&
          store.getState().isAuthenticated
        ) {
          resetLogoutInactivityTimeout();
        }

        // Track if user is Idle during their exercise to end the session
        if (getCurrentRoute()?.name === 'ExerciseProcess') {
          resetEndPracticeSessionAfterIdleTime();
        }
      },
    }),
  ).current;

  const resetLogoutInactivityTimeout = () => {
    clearTimeout(timerForLogoutUserId.current);

    timerForLogoutUserId.current = setTimeout(async () => {
      if (store.getState().isAuthenticated && !store.getState().stayLoggedIn) {
        await serverLogoutUser({ jwt: (await getAccessToken()) ?? '' });
        await localLogoutUser();
        await clearLocalData();
      }
    }, timeForInactivityToLogoutUserInSecond);
  };

  const resetEndPracticeSessionAfterIdleTime = () => {
    clearTimeout(timerForEndUserSessionId.current);

    timerForEndUserSessionId.current = setTimeout(async () => {
      if (store.getState().isAuthenticated && !store.getState().stayLoggedIn) {
        await serverLogoutUser({ jwt: await getAccessToken() });
        await localLogoutUser();
        await clearLocalData();
      }
      if (getCurrentRoute()?.name === 'ExerciseProcess') {
        utils.pushPracticeSessionTrackAndReset();
      }
    }, timeForInactivityToEndSessionInSecond);
  };

  return (
    <SafeAreaProvider>
      <SafeAreaView style={styles.layoutChildren} {...panResponder.panHandlers}>
        {constants.screenshotPreventionEnabled ? (
          <ScreenshotPrevention>{children}</ScreenshotPrevention>
        ) : (
          children
        )}
      </SafeAreaView>
    </SafeAreaProvider>
  );
}
